'use client';

import classNames from 'classnames';
import type { ComponentProps, ReactNode } from 'react';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import styles from './ToutSection.module.scss';

type ImageSrc = ComponentProps<typeof Image>['src'];
type ButtonProps = Omit<ComponentProps<typeof Button>, 'size' | 'text' | 'variant'>;

type ToutSectionProps = {
  altText: string;
  buttonProps: ButtonProps;
  buttonText: ReactNode;
  contentLayout?: 'default' | 'wide';
  desktopImage: ImageSrc;
  mobileImage?: ImageSrc;
  subtitle: ReactNode;
  title: ReactNode;
  logoAltText?: string;
  logoSrc?: ImageSrc;
};

const ToutSection = ({
  altText,
  buttonProps,
  buttonText,
  contentLayout = 'default',
  desktopImage,
  mobileImage,
  subtitle,
  title,
  logoAltText,
  logoSrc
}: ToutSectionProps) => {
  const isMobile = useIsMobileSizedScreen();
  const { descriptionVariant, headingSize, imageSrc } = (
    {
      desktop: {
        descriptionVariant: 'paragraph200',
        headingSize: '500',
        imageSrc: desktopImage
      },
      mobile: {
        descriptionVariant: 'paragraph100',
        headingSize: '400',
        imageSrc: mobileImage ?? desktopImage
      }
    } as const
  )[isMobile ? 'mobile' : 'desktop'];

  const image = <Image alt={altText} className={styles.image} sizes="100vw" src={imageSrc} />;
  const logo = logoAltText && logoSrc ? <Image alt={logoAltText} src={logoSrc} /> : null;

  return (
    <div className={styles.container}>
      <div className={classNames(styles.imageWrapper, contentLayout === 'wide' && styles.wideContent)}>
        {contentLayout === 'wide' ? <div className={styles.imageContainer}>{image}</div> : image}
        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            {logo}
            <Heading as="h2" className={styles.title} size={headingSize}>
              {title}
            </Heading>
            <Typography className={styles.description} component="p" variant={descriptionVariant}>
              {subtitle}
            </Typography>
            <Button size="md" text={buttonText} variant="accent" {...buttonProps} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToutSection;
